import * as React from "react";
import { useCallback, useRef, useState } from "react";
import useLazyLoad from "./shared/useLazyLoad";

export interface SmartBackgroundImageProps {
    imageUrlFunc: (width: number, height: number) => string;
    className?: string;
    link?: string;
    onClick?: (e) => void;
    title?: string;
    autoSizing?: boolean;
    imageWidth?: number;
    imageHeight?: number;
}

export default function SmartBackgroundImage({
    imageUrlFunc,
    link,
    className,
    title,
    onClick,
    children,
    autoSizing,
    imageWidth,
    imageHeight,
}: React.PropsWithChildren<SmartBackgroundImageProps>) {
    const containerRef = useRef<HTMLDivElement>();
    const [imageUrl, setImageUrl] = useState<string>();
    const [imageLoaded, setImageLoaded] = useState(false);
    const [visible, setVisible] = useState(false);
    
    const handleVisible = useCallback(() => {
        setVisible(true);
        // We use autoSizing if autoSizing is requested or the caller
        // didn't supply an image width (which is required if not using auto sizing)
        if (autoSizing || !imageWidth) {
            setImageUrl(
                imageUrlFunc(
                    Math.round(containerRef.current.offsetWidth * 1.25),
                    Math.round(containerRef.current.offsetHeight * 1.25)
                )
            );
        } else if (imageWidth) {
            setImageUrl(imageUrlFunc(imageWidth, imageHeight));
        }
    }, [imageUrlFunc, autoSizing, imageWidth, imageHeight]);

    useLazyLoad(containerRef, handleVisible);

    const handleClick = useCallback(
        (e) => {
            if (onClick) {
                onClick(e);
            }
            if (link) {
                window.location.href = link;
            }
        },
        [link, onClick]
    );

    const handleImageLoaded = useCallback(() => {
        setImageLoaded(true);
    }, []);

    // If the complete imageurl is not loaded yet, get a very small image to show until then
    const url = imageLoaded
        ? "url(" + imageUrl + ")"
        : "url(" + imageUrlFunc(12, 0) + ")";

    return (
        <div
            tabIndex={link ? 0 : -1}
            ref={containerRef}
            role={link ? "link" : null}
            className={className}
            onClick={handleClick}
            title={title || ""}
            aria-label={title || null}
        >
            {imageUrl && visible && (
                <>
                    <div
                        style={{
                            backgroundImage: url,
                            width: "100%",
                            height: "100%",
                            backgroundSize: "cover",
                            backgroundPosition: "center center"
                        }}
                    >
                        {children}
                    </div>
                    <img
                        src={imageUrl}
                        onLoad={handleImageLoaded}
                        style={{ display: "none" }}
                    ></img>
                </>
            )}
        </div>
    );
}
